import useGoogleMaps from "@insite/client-framework/Common/Hooks/useGoogleMaps";
import useLocationFilterSearch from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import useLocationGoogleMarkers, {
    CurrentLocationInfoWindow,
    LocationInfoWindow,
} from "@insite/client-framework/Common/Hooks/useLocationGoogleMarkers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getGeoCodeFromAddress,
    getGeoCodeFromLatLng,
} from "@insite/client-framework/Common/Utilities/GoogleMaps/getGeoCodeFromAddress";
import withDynamicGoogleMaps from "@insite/client-framework/Common/withDynamicGoogleMaps";
import { GetDealersApiParameter } from "@insite/client-framework/Services/DealerService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getDealersDataView,
    getDealersDefaultLocation,
} from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setParameter from "@insite/client-framework/Store/Pages/LocationFinder/Handlers/SetParameter";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import { DealerModel, PaginationModel } from "@insite/client-framework/Types/ApiModels";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ADIBranchSearchForm from "@NA/Components/ProductList/ADIBranchSearchForm";
import setMyBranch from "@NA/Handlers/ADISetMyBranchHandler";
import { LocationModel } from "@NA/Hooks/useLocationFilterSearchCustom";
import customLoadDealers from "@NA/Overrides/Store/Data/Dealers/Handlers/LoadDealers";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps extends WidgetProps {
    useLoadScript: ({ googleMapsApiKey }: { googleMapsApiKey: any }) => { isLoaded: boolean };
    modalCloseHandler: () => void;
    myBranchDetail: string;
}

const mapDispatchToProps = {
    customLoadDealers,
    setParameter,
    clearAllProductFilters,
    setMyBranch,
};

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
    settings: getSettingsCollection(state),
    getDealersParameter: state.pages.locationFinder.getDealersParameter,
    dealersDataView: getDealersDataView(state, state.pages.locationFinder.getDealersParameter),
    dealersDefaultLocation: getDealersDefaultLocation(state),
    dealerDetailsUrl: getPageLinkByPageType(state, "DealerDetailsPage")?.url,
    currentUrl: getLocation(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface ADIChangeBranchStyles {
    container?: InjectableCss;
}

export const adiChangeBranchStyles: ADIChangeBranchStyles = {};

const styles = adiChangeBranchStyles;

const ADIChangeBranch: FC<Props> = ({
    settings,
    dealersDataView,
    dealersDefaultLocation,
    getDealersParameter,
    customLoadDealers,
    setParameter,
    useLoadScript,
    clearAllProductFilters,
    setMyBranch,
    currentUrl,
    modalCloseHandler,
    myBranchDetail,
}) => {
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [radius, setRadius] = React.useState<number | undefined>(100);
    const [isSearchClicked, setIsSearchClicked] = React.useState<boolean>(false);

    const handleLoadLocations = (parameter: GetDealersApiParameter) => {
        setParameter(parameter);
    };

    React.useEffect(() => {
        if (!isSearchClicked) {
            return;
        }
        if (getDealersParameter) {
            customLoadDealers(getDealersParameter as any);
        }
    }, [getDealersParameter]);

    // Manage Google Maps State
    const { googleMapsApiKey } = settings.websiteSettings;
    const { isLoaded } = useLoadScript({ googleMapsApiKey });
    const { googleMap, currentLocation, setCurrentLocation, isGoogleMapsScriptsLoaded, setBounds, setMapCenter } =
        useGoogleMaps({
            isShown: true,
            isLoaded,
        });

    // Manage Selected Dealer
    const [selectedDealer, setSelectedDealer] = React.useState<DealerModel | undefined>(undefined);
    const [showSelectedDealer, setShowSelectedDealer] = React.useState<boolean>(false);
    const [defaultRadius, setDefaultRadius] = React.useState<number>(0);

    // Manage Local Dealers
    const [dealers, setDealers] = React.useState<DealerModel[]>([]);
    const [locationsPagination, setLocationsPagination] = React.useState<PaginationModel | undefined>();

    // Manage Loading Dealers on State Changes
    const onSearch = (searchFilter: string) => {
        setLocationSearchFilter(searchFilter);
    };

    const createFilter = (coords: google.maps.LatLng | undefined, searchFilter: string): GetDealersApiParameter => {
        // Default center Point
        let centerPoint = { latitude: 0, longitude: 0 };
        // Passed in coords
        if (coords && coords.lat() !== 0 && coords.lng() !== 0) {
            centerPoint = {
                latitude: coords.lat(),
                longitude: coords.lng(),
            };
        }
        //  Selected Dealer
        if (centerPoint.latitude === 0 && centerPoint.longitude === 0 && selectedDealer) {
            centerPoint = {
                ...selectedDealer,
            };
        }
        // default Location
        if (
            centerPoint.latitude === 0 &&
            centerPoint.longitude === 0 &&
            dealersDefaultLocation.latitude !== 0 &&
            dealersDefaultLocation.latitude !== 0
        ) {
            centerPoint = dealersDefaultLocation;
        }

        return {
            name: searchFilter,
            latitude: centerPoint.latitude,
            longitude: centerPoint.longitude,
            page,
            pageSize: 5,
            radius,
        };
    };

    const { doSearch, locationSearchFilter, setLocationSearchFilter, page, setPage, pageSize, setPageSize } =
        useLocationFilterSearch<DealerModel, GetDealersApiParameter>({
            onSearch,
            loadLocations: handleLoadLocations,
            currentLocation,
            defaultRadius,
            selectedLocation: selectedDealer,
            setSelectedLocation: location => setSelectedDealer(location),
            setShowSelectedLocation: setShowSelectedDealer,
            createFilter,
        });
    React.useEffect(() => {
        if (!isGoogleMapsScriptsLoaded) {
            return;
        }
        if (dealersDataView.value && !dealersDataView.isLoading) {
            const { pagination, value: dealers, defaultRadius, defaultLatitude, defaultLongitude } = dealersDataView;
            // Find default location
            setLocationSearchFilter(locationSearchFilter);
            setLocationsPagination(pagination || undefined);
            setDefaultRadius(defaultRadius);
            setMapCenter(new google.maps.LatLng(defaultLatitude, defaultLongitude));
            setDealers(dealers || []);
            setIsLoading(false);
        }
    }, [dealersDataView, isGoogleMapsScriptsLoaded]);

    // Manage Bounds of Map
    React.useEffect(() => {
        const bounds = [];
        closeInfoWindows();
        dealers.forEach(dealer => {
            bounds.push({ lat: dealer.latitude, lng: dealer.longitude });
        });
        if (showSelectedDealer && selectedDealer) {
            bounds.push({ lat: selectedDealer.latitude, lng: selectedDealer.longitude });
        }
        if (currentLocation && currentLocation.lat() !== 0 && currentLocation.lng() !== 0) {
            bounds.push({ lat: currentLocation.lat(), lng: currentLocation.lng() });
        }
        setBounds(bounds);
    }, [googleMap, dealers, selectedDealer, showSelectedDealer, currentLocation]);

    const [currentLocationInfoWindow, setCurrentLocationInfoWindow] = React.useState<
        CurrentLocationInfoWindow | undefined
    >(undefined);
    const [locationInfoWindow, setLocationInfoWindow] = React.useState<LocationInfoWindow<DealerModel> | undefined>(
        undefined,
    );
    const setLocationOfInfoWindow = (location: DealerModel | undefined) => {
        if (!location) {
            setLocationInfoWindow(undefined);
            return;
        }
        setLocationInfoWindow({
            position: new google.maps.LatLng(location.latitude, location.longitude),
            location,
        });
    };
    // Manage Map Markers
    const { closeInfoWindows } = useLocationGoogleMarkers({
        locations: dealers,
        locationSearchFilter,
        locationsPagination,
        currentLocation,
        selectedLocation: selectedDealer,
        showSelectedLocation: showSelectedDealer,
        setLocationOfInfoWindow,
        setCurrentLocationInfoWindow,
    });

    const [geoLocationSearchText, setGeoLocationSearchText] = React.useState("");
    const [showGeoLocationErrorMessage, setShowGeoLocationErrorMessage] = React.useState(false);

    const handleGeoLocationSearch = (geoLocationSearch: string) => {
        setShowGeoLocationErrorMessage(false);
        // Lookup and set currentLocation to this search text
        getGeoCodeFromAddress(geoLocationSearch)
            .then(result => {
                const geocoderResult = result[0];
                if (geocoderResult.formatted_address) {
                    setGeoLocationSearchText(geocoderResult.formatted_address);
                }

                setPage(1);
                setCurrentLocation(geocoderResult.geometry.location);
            })
            .catch(() => {
                setShowGeoLocationErrorMessage(true);
            });
    };

    const handleSearch = (geoLocationSearch: string, locationSearch: string) => {
        if (!geoLocationSearch) {
            setShowGeoLocationErrorMessage(true);
            return;
        }
        setIsSearchClicked(true);
        setShowGeoLocationErrorMessage(false);
        setLocationSearchFilter(locationSearch);
        handleGeoLocationSearch(geoLocationSearch);
        return;
    };

    const handleSelect = (location: LocationModel) => {
        clearAllProductFilters();
        const warehouseCode = location.properties.admnDc || "";
        const attrId = location.webSiteUrl?.toLowerCase();
        if (window) {
            window.localStorage.setItem("warehouseCode", warehouseCode);
            window.localStorage.setItem("warehouseDisplayName", `${location.name}, ${location.state}`);
            window.localStorage.setItem("warehouseNameSection", `${location.name}`);
        }
        setMyBranch({
            warehouseCode: warehouseCode,
            warehouseDisplayName: `${location.name}, ${location.state}`,
            warehouseNameSection: `${location.name}`,
        });
        modalCloseHandler();
        const url = `${currentUrl.pathname}?attributes=${attrId}`;
        window.location.href = url;
    };

    React.useEffect(() => {
        setGeoLocationSearchText(myBranchDetail);
    }, []);

    return (
        <StyledWrapper {...styles.container} data-test-selector="branchFinder">
            <ADIBranchSearchForm
                isLoading={isLoading}
                onSearch={handleSearch}
                onSelect={handleSelect}
                locationSearchFilter={locationSearchFilter}
                geoLocationSearchText={geoLocationSearchText}
                showGeoLocationErrorMessage={showGeoLocationErrorMessage}
                geoLocationErrorMessage={siteMessage("DealerLocator_GeocodeErrorMessage")}
                locations={dealers}
                setRadius={setRadius}
                isSearchClicked={isSearchClicked}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withDynamicGoogleMaps(ADIChangeBranch)));
